import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, CircularProgress,
    List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
    Avatar, Chip, InputAdornment, IconButton, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import moment from 'moment';
import {
    getOrdersForLinkingRequest,
    linkStudyRequest,
    resetOrdersForLinking,
} from '../../../redux/modules/allStudies/slice';

const StudyInfoCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

const OrderListItem = styled(ListItemButton)(({ theme, selected }) => ({
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    border: `2px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
    backgroundColor: selected ? 'rgba(105, 203, 249, 0.08)' : theme.palette.background.subtle || theme.palette.grey[50],
    '&:hover': {
        backgroundColor: selected ? 'rgba(105, 203, 249, 0.12)' : theme.palette.action.hover,
    },
}));

const LinkToOrderDialog = ({ open, onClose, study }) => {
    const dispatch = useDispatch();
    const { ordersForLinking, ordersForLinkingLoading, linkLoading } = useSelector(state => state.allStudies);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        if (open) {
            dispatch(getOrdersForLinkingRequest({ search: '', page: 1, limit: 50 }));
        }
        return () => {
            dispatch(resetOrdersForLinking());
            setSelectedOrder(null);
            setSearchTerm('');
        };
    }, [open, dispatch]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        dispatch(getOrdersForLinkingRequest({ search: value, page: 1, limit: 50 }));
    };

    const handleSelectOrder = (order) => {
        setSelectedOrder(order);
    };

    const handleLink = () => {
        if (!study?.orthancStudyId || !selectedOrder?._id) return;
        dispatch(linkStudyRequest({
            orthancStudyId: study.orthancStudyId,
            orderId: selectedOrder._id,
        }));
    };

    if (!study) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                    Link Study to Existing Order
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* Study Information */}
                <StudyInfoCard>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                        Study to Link
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                        <Box>
                            <InfoLabel>Patient Name</InfoLabel>
                            <InfoValue>{study.patientNameDisplay || 'Unknown'}</InfoValue>
                        </Box>
                        <Box>
                            <InfoLabel>Study Date</InfoLabel>
                            <InfoValue>
                                {study.studyDate ? moment(study.studyDate).format('MMM DD, YYYY') : '—'}
                            </InfoValue>
                        </Box>
                        <Box>
                            <InfoLabel>Modality</InfoLabel>
                            <InfoValue>{study.modality || 'US'}</InfoValue>
                        </Box>
                        <Box>
                            <InfoLabel>Images</InfoLabel>
                            <InfoValue>{study.instanceCount || 0}</InfoValue>
                        </Box>
                    </Box>
                    {study.studyDescription && (
                        <Box sx={{ mt: 2 }}>
                            <InfoLabel>Description</InfoLabel>
                            <InfoValue>{study.studyDescription}</InfoValue>
                        </Box>
                    )}
                </StudyInfoCard>

                <Divider sx={{ my: 2 }} />

                {/* Order Search */}
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    Select Order to Link
                </Typography>
                <TextField
                    fullWidth
                    placeholder="Search by order ID or patient name..."
                    value={searchTerm}
                    onChange={handleSearch}
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: ordersForLinkingLoading && (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Orders List */}
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {ordersForLinking.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">
                                {ordersForLinkingLoading ? 'Loading orders...' : 'No orders available for linking'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Orders without images will appear here
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {ordersForLinking.map((order) => (
                                <OrderListItem
                                    key={order._id}
                                    selected={selectedOrder?._id === order._id}
                                    onClick={() => handleSelectOrder(order)}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: selectedOrder?._id === order._id ? 'primary.main' : 'grey.300' }}>
                                            <PersonIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography fontWeight={500}>
                                                    {order.patient?.name || 'Unknown Patient'}
                                                </Typography>
                                                <Chip
                                                    label={order.orderId}
                                                    size="small"
                                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption" display="block">
                                                    {order.patient?.mrn && `MRN: ${order.patient.mrn} | `}
                                                    {order.patient?.dob && `DOB: ${moment(order.patient.dob).format('MMM DD, YYYY')}`}
                                                </Typography>
                                                {order.exams && (
                                                    <Typography variant="caption" color="primary">
                                                        {order.exams}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    <Chip
                                        label={order.status || 'Scheduled'}
                                        size="small"
                                        color={order.status === 'scheduled' ? 'info' : 'default'}
                                    />
                                </OrderListItem>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Selected Order Preview */}
                {selectedOrder && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
                            Selected Order: {selectedOrder.orderId}
                        </Typography>
                        <Typography variant="body2" color="primary.dark">
                            Patient: {selectedOrder.patient?.name} | Status: {selectedOrder.status}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={onClose} disabled={linkLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleLink}
                    disabled={!selectedOrder || linkLoading}
                    startIcon={linkLoading ? <CircularProgress size={16} /> : null}
                >
                    {linkLoading ? 'Linking...' : 'Link Study to Order'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkToOrderDialog;
